import { useSearchIcd10Codes } from "@/clinical-codes/useSearchIcd10Codes";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useCustomers } from "@/customers/useCustomers";
import { useDebounce } from "@/hooks/use-debounce";
import { RFL_AUTOMATION_LABEL } from "@/specialties/rflAutomation";
import { Constants } from "@/integrations/supabase/types";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Icd10CodeRule, RuleSpecialty, UpsertRuleData } from "./useIcd10CodeRules";
import type { UseMutationResult } from "@tanstack/react-query";

export const specialtyLabel = (specialty: string) =>
  specialty === "rfl_automation"
    ? RFL_AUTOMATION_LABEL
    : `${specialty.charAt(0).toUpperCase()}${specialty.slice(1).replaceAll("_", " ")}`;

const SPECIALTY_OPTIONS = Constants.public.Enums.encounter_speciality_type.map((specialty) => ({
  value: specialty,
  label: specialtyLabel(specialty),
}));

interface UpsertIcd10CodeRuleProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: Icd10CodeRule | null;
  onSuccess?: () => void;
  upsertRule?: UseMutationResult<Icd10CodeRule | null, Error, { ruleData: UpsertRuleData; ruleId?: string }, unknown>;
}

export const UpsertIcd10CodeRule = ({ open, onOpenChange, rule, onSuccess, upsertRule }: UpsertIcd10CodeRuleProps) => {
  const _queryClient = useQueryClient();
  const isEditing = !!rule;

  // Form state
  const [name, setName] = useState(rule?.name || "");
  const [ruleText, setRuleText] = useState(rule?.rule_text || "");
  const [isActive, setIsActive] = useState(rule?.is_active ?? true);
  const [customerIds, setCustomerIds] = useState<string[]>(
    rule?.icd10CodeRuleCustomers.map((c) => c.customer_id) ?? [],
  );
  const [specialties, setSpecialties] = useState<RuleSpecialty[]>(
    rule?.icd10CodeRuleSpecialties.map((s) => s.specialty) ?? [],
  );
  const { customers, isLoading: customersLoading } = useCustomers();
  const customerOptions = customers.map((customer) => ({ value: customer.id, label: customer.label }));
  const selectedCustomers = customerIds.map(
    (id) => customerOptions.find((option) => option.value === id) ?? { value: id, label: id },
  );
  const selectedSpecialties = SPECIALTY_OPTIONS.filter((option) => specialties.includes(option.value));
  const [selectedCodes, setSelectedCodes] = useState<
    Array<{
      id: string;
      code: string;
      description: string;
    }>
  >([]);

  // Update state if the rule prop changes
  useEffect(() => {
    if (open && rule) {
      setName(rule.name);
      setRuleText(rule.rule_text);
      setIsActive(rule.is_active);
      setCustomerIds(rule.icd10CodeRuleCustomers.map((c) => c.customer_id));
      setSpecialties(rule.icd10CodeRuleSpecialties.map((s) => s.specialty));
      setSelectedCodes(
        rule.icd10CodeRuleAssignments.map((assignment) => ({
          id: assignment.icd10Code.id,
          code: assignment.icd10Code.code,
          description: assignment.icd10Code.description,
        })),
      );
    }
  }, [rule, open]);

  // ICD-10 code search
  const [codeSearch, setCodeSearch] = useState("");
  const debouncedCodeSearch = useDebounce(codeSearch, 300);
  const { codes: searchResults, isLoading: isSearching } = useSearchIcd10Codes(debouncedCodeSearch);

  const handleClose = () => {
    setName("");
    setRuleText("");
    setIsActive(true);
    setCustomerIds([]);
    setSpecialties([]);
    setSelectedCodes([]);
    setCodeSearch("");
    onOpenChange(false);
  };

  const handleAddCode = (code: { id: string; code: string; description: string }) => {
    if (!selectedCodes.find((c) => c.id === code.id)) {
      setSelectedCodes((prev) => [...prev, code]);
    }
    setCodeSearch("");
  };

  const handleRemoveCode = (codeId: string) => {
    setSelectedCodes((prev) => prev.filter((c) => c.id !== codeId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!upsertRule) {
      toast.error("Upsert function not available");
      return;
    }

    try {
      await upsertRule.mutateAsync({
        ruleData: {
          name,
          rule_text: ruleText,
          is_active: isActive,
          codeIds: selectedCodes.map((code) => code.id),
          customerIds,
          specialties,
          expectedUpdatedAt: rule?.updated_at,
        },
        ruleId: rule?.id,
      });

      // Close dialog and call success callback
      onSuccess?.();
      handleClose();
    } catch (error) {
      // Error is already handled by the mutation
      console.error("Error upserting rule:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : handleClose())}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit ICD-10 Code Rule" : "Create New ICD-10 Code Rule"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update the rule details, its ICD-10 codes, and the customers and specialties it applies to."
              : "Create a new rule, assign ICD-10 codes to it, and optionally limit it to customers and specialties."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            {/* Rule Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Rule Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter rule name"
                required
              />
            </div>

            {/* Rule Text */}
            <div className="space-y-2">
              <Label htmlFor="ruleText">Rule Text *</Label>
              <Textarea
                id="ruleText"
                value={ruleText}
                onChange={(e) => setRuleText(e.target.value)}
                placeholder="Enter rule description or logic"
                rows={4}
                required
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center space-x-2">
              <Switch id="isActive" checked={isActive} onCheckedChange={setIsActive} />
              <Label htmlFor="isActive">Active</Label>
            </div>

            <div className="space-y-2">
              <Label>Customers</Label>
              <MultiSelect
                options={customerOptions}
                value={selectedCustomers}
                onChange={(selected) => setCustomerIds(selected.map((option) => option.value))}
                placeholder="All customers"
                isLoading={customersLoading}
              />
              <p className="text-sm text-muted-foreground">Leave empty to apply the rule to all customers.</p>
            </div>

            <div className="space-y-2">
              <Label>Specialties</Label>
              <MultiSelect
                options={SPECIALTY_OPTIONS}
                value={selectedSpecialties}
                onChange={(selected) => setSpecialties(selected.map((option) => option.value as RuleSpecialty))}
                placeholder="All specialties"
              />
              <p className="text-sm text-muted-foreground">Leave empty to apply the rule to all specialties.</p>
            </div>

            {/* ICD-10 Code Selection */}
            <div className="space-y-4">
              <Label>Associated ICD-10 Codes *</Label>

              {/* Selected Codes Display */}
              {selectedCodes.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Selected Codes ({selectedCodes.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedCodes.map((code) => (
                      <Badge key={code.id} variant="secondary" className="flex items-center gap-1">
                        <span className="font-mono">{code.code}</span>
                        <span className="max-w-[200px] truncate">{code.description}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-auto p-0 ml-1"
                          onClick={() => handleRemoveCode(code.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Code Search */}
              <div className="space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={codeSearch}
                    onChange={(e) => setCodeSearch(e.target.value)}
                    placeholder="Search ICD-10 codes..."
                    className="pl-10"
                  />
                </div>

                {/* Search Results */}
                {codeSearch && (
                  <ScrollArea className="h-48 border rounded-md">
                    <div className="p-2 space-y-1">
                      {isSearching ? (
                        <div className="text-sm text-muted-foreground p-2">Searching...</div>
                      ) : searchResults && searchResults.length > 0 ? (
                        searchResults
                          .filter((code) => !selectedCodes.find((selected) => selected.id === code.id))
                          .slice(0, 20) // Limit results for performance
                          .map((code) => (
                            <Button
                              key={code.id}
                              type="button"
                              variant="ghost"
                              className="w-full justify-start h-auto p-2 text-left"
                              onClick={() => handleAddCode(code)}
                            >
                              <div className="flex items-center gap-2">
                                <Plus className="h-3 w-3" />
                                <span className="font-mono">{code.code}</span>
                                <span className="text-muted-foreground truncate">{code.description}</span>
                              </div>
                            </Button>
                          ))
                      ) : (
                        <div className="text-sm text-muted-foreground p-2">No codes found</div>
                      )}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={upsertRule?.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={upsertRule?.isPending || !name.trim() || !ruleText.trim() || selectedCodes.length === 0}
            >
              {upsertRule?.isPending
                ? isEditing
                  ? "Updating..."
                  : "Creating..."
                : isEditing
                  ? "Update Rule"
                  : "Create Rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
